import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, type ActionPerformed, type PushNotificationSchema, type RegistrationError, type Token } from '@capacitor/push-notifications';
import { supabase } from '@/lib/supabase';

const DEFAULT_PUSH_CHANNEL = 'lafete-alerts';
// Token FCM de CET appareil, mémorisé pour pouvoir le retirer de push_tokens
// à la déconnexion sans toucher aux tokens des autres appareils.
const DEVICE_TOKEN_KEY = 'lafete:device-push-token';

type PushHandlers = {
  onNotification?: (notification: PushNotificationSchema) => void;
  onAction?: (action: ActionPerformed) => void;
  onRegistrationError?: (error: RegistrationError) => void;
};

let listenersReady = false;
let tokenCallback: ((token: string) => Promise<void> | void) | null = null;

function isNativePushAvailable() {
  return Capacitor.isNativePlatform();
}

function eventIdFromNotificationData(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const maybeEventId = (data as Record<string, unknown>).event_id;
  return typeof maybeEventId === 'string' && maybeEventId.length > 0 ? maybeEventId : null;
}

export async function ensurePushChannel() {
  if (Capacitor.getPlatform() !== 'android') return;

  try {
    await PushNotifications.createChannel({
      id: DEFAULT_PUSH_CHANNEL,
      name: 'Alertes La Fete',
      description: 'Nouveaux evenements et alertes suivies',
      importance: 4,
      visibility: 1,
      vibration: true,
    });
  } catch {}
}

export async function registerNativePushListeners(handlers: PushHandlers = {}) {
  if (!isNativePushAvailable() || listenersReady) return;

  await ensurePushChannel();

  await PushNotifications.addListener('registration', async (token: Token) => {
    if (!token.value) return;
    await tokenCallback?.(token.value);
  });

  await PushNotifications.addListener('registrationError', (error: RegistrationError) => {
    console.error('[push] registration error', error);
    handlers.onRegistrationError?.(error);
  });

  await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.info('[push] received', notification);
    handlers.onNotification?.(notification);
  });

  await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.info('[push] action', action);
    handlers.onAction?.(action);

    const eventId = eventIdFromNotificationData(action.notification.data);
    if (eventId) {
      window.location.assign(`/event/${eventId}`);
    }
  });

  await App.addListener('appUrlOpen', ({ url }) => {
    if (!url) return;

    try {
      const parsed = new URL(url);
      const eventId = parsed.searchParams.get('event_id');
      if (eventId) window.location.assign(`/event/${eventId}`);
    } catch {}
  });

  listenersReady = true;
}

export async function requestAndRegisterNativePush(onToken?: (token: string) => Promise<void> | void) {
  if (!isNativePushAvailable()) return { granted: false };

  tokenCallback = onToken ?? null;

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt') {
    permission = await PushNotifications.requestPermissions();
  }

  if (permission.receive !== 'granted') {
    return { granted: false };
  }

  await ensurePushChannel();
  await PushNotifications.register();
  return { granted: true };
}

export async function syncPushTokenForUser(userId: string, token: string) {
  try { window.localStorage.setItem(DEVICE_TOKEN_KEY, token); } catch { /* stockage indisponible */ }
  // Table multi-appareils : un token par (utilisateur, appareil).
  const { error: tokenError } = await supabase.from('push_tokens').upsert(
    { user_id: userId, token, platform: Capacitor.getPlatform(), last_seen_at: new Date().toISOString() },
    { onConflict: 'user_id,token' },
  );
  if (tokenError) throw tokenError;
  // Colonne historique conservée comme fallback des dispatchers pendant la transition.
  const { error } = await supabase.from('profiles').update({ push_token: token }).eq('id', userId);
  if (error) throw error;
}

export async function syncNotificationsOptIn(userId: string, enabled: boolean) {
  const { error } = await supabase
    .from('profiles')
    .update({ notifications_opt_in: enabled })
    .eq('id', userId);
  if (error) throw error;
}

export async function clearPushRegistration(userId?: string | null) {
  if (userId) {
    let deviceToken: string | null = null;
    try { deviceToken = window.localStorage.getItem(DEVICE_TOKEN_KEY); } catch { /* ignore */ }
    if (deviceToken) {
      await supabase.from('push_tokens').delete().eq('user_id', userId).eq('token', deviceToken);
    }
    await supabase.from('profiles').update({ push_token: null, notifications_opt_in: false }).eq('id', userId);
  }
  try { window.localStorage.removeItem(DEVICE_TOKEN_KEY); } catch { /* ignore */ }

  if (!isNativePushAvailable()) return;

  try {
    await PushNotifications.unregister();
  } catch {}
}